
import { schema } from "normalizr";
import { Record, Map } from "immutable";

import { Organisation, IOrganisation, AuthNormalisedEntities } from "@auth/models";
import { IImage, Image, FilesNormalisedEntities } from "@files/models";
import { TranslatableMap } from "@localisation/models";
import { RestModel, RestRecord, NormalisedEntities } from "@orm/models";

export interface IAlbum extends RestModel {
    id?: number;
    title?: string;
    titleVersion?: string;
    upc?: string;
    formatName?: string;
    statusName?: string;
    customId?: string;
    genreId?: number;
    additionalGenreId?: number;
    labelId?: number;
    artworkId?: number;
    copyrightArtwork?: string;
    copyrightArtworkYear?: number;
    copyrightSound?: string;
    copyrightSoundYear?: number;
    originalReleaseDate?: Date;
    preorderDate?: Date;
    releaseDate?: Date;
    preorderPreview?: boolean;
    comment?: string;
    commentPrivate?: string;
    readyForDelivery?: boolean;
    organisationId?: number;
    priceId?: number;
    trackPriceId?: number;
    price?: any;
    trackPrice?: any;
    createdAt?: Date;
    updatedAt?: Date;
    deletedAt?: Date;
    // Non-DB data
    artistsStr?: string;
    // Relationships
    artists?: number[] | IArtist[];
    tracks?: number[] | ITrack[];
    artwork?: IImage;
    format?: IAlbumFormat;
    status?: IAlbumStatus;
    genre?: IGenre;
    additionalGenre?: IGenre;
    organisation?: IOrganisation;
    label?: ILabel;
    pivot?: IAlbumTrack;
}

export interface ITrack extends RestModel {
    id?: number;
    title?: string;
    titleVersion?: string;
    isrc?: string;
    genreId?: number;
    labelId?: number;
    explicit?: boolean;
    gapless?: boolean;
    readyForDelivery?: boolean;
    rightAvailable?: boolean;
    copyrightSound?: string;
    copyrightSoundYear?: number;
    duration?: number;
    organisationId?: number;
    createdAt?: Date;
    updatedAt?: Date;
    deletedAt?: Date;
    // Non-DB data
    artistsStr?: string;
    durationFormatted?: string;
    // Relationships
    artists?: number[] | IArtist[];
    albums?: number[] | IAlbum[];
    genre?: IGenre;
    label?: ILabel;
    pivot?: IAlbumTrack;
}

export interface IArtist extends RestModel {
    id?: number;
    name?: string;
    publish?: boolean;
    createdAt?: Date;
    updatedAt?: Date;
    deletedAt?: Date;
    image?: string;
    // Relationships
    pivot?: IArtistPivot;
    albums?: number[] | IAlbum[];
}

export interface IGenre {
    id?: number;
    name?: string;
    label?: TranslatableMap;
    createdAt?: Date;
    updatedAt?: Date;
    deletedAt?: Date;
}

export interface IAlbumFormat {
    name?: string;
    label?: TranslatableMap;
}

export interface IAlbumStatus {
    name?: string;
    label?: TranslatableMap;
}

export interface IArtistRole {
    name?: string;
    label?: TranslatableMap;
}

// Pivot models
export interface IAlbumTrack {
    albumId?: number;
    trackId?: number;
    disc?: number;
    track?: number;
    albumOnly?: boolean;
    preorderDate?: Date;
    releaseDate?: Date;
}

export interface IArtistPivot {
    artistId?: number;
    roleName?: string;
    isMain?: boolean;
    order?: number;
    role?: IArtistRole;
}

export interface IAlbumArtist extends IArtistPivot {
    albumId?: number;
}

export interface ITrackArtist extends IArtistPivot {
    trackId?: number;
}

export interface ILabel {
    id?: number;
    name?: string;
}

const AlbumRecord = RestRecord({
    id:                   undefined,
    title:                undefined,
    titleVersion:         undefined,
    upc:                  undefined,
    formatName:           undefined,
    statusName:           undefined,
    customId:             undefined,
    genreId:              undefined,
    additionalGenreId:    undefined,
    labelId:              undefined,
    artworkId:            undefined,
    copyrightArtwork:     undefined,
    copyrightArtworkYear: undefined,
    copyrightSound:       undefined,
    copyrightSoundYear:   undefined,
    originalReleaseDate:  undefined,
    preorderDate:         undefined,
    releaseDate:          undefined,
    preorderPreview:      undefined,
    comment:              undefined,
    commentPrivate:       undefined,
    readyForDelivery:     undefined,
    organisationId:       undefined,
    priceId:              undefined,
    trackPriceId:         undefined,
    price:                undefined,
    trackPrice:           undefined,
    createdAt:            undefined,
    updatedAt:            undefined,
    deletedAt:            undefined,
    artistsStr:           undefined,
    artists:              undefined,
    tracks:               undefined,
    artwork:              undefined,
    format:               undefined,
    status:               undefined,
    genre:                undefined,
    additionalGenre:      undefined,
    organisation:         undefined,
    label:                undefined,
    pivot:                undefined,
});

const TrackRecord = RestRecord({
    id:                 undefined,
    title:              undefined,
    titleVersion:       undefined,
    isrc:               undefined,
    genreId:            undefined,
    labelId:            undefined,
    explicit:           undefined,
    gapless:            undefined,
    readyForDelivery:   undefined,
    rightAvailable:     undefined,
    copyrightSound:     undefined,
    copyrightSoundYear: undefined,
    duration:           undefined,
    organisationId:     undefined,
    createdAt:          undefined,
    updatedAt:          undefined,
    deletedAt:          undefined,
    artistsStr:         undefined,
    durationFormatted:  undefined,
    artists:            undefined,
    albums:             undefined,
    genre:              undefined,
    label:              undefined,
    pivot:              undefined,
});

const ArtistRecord = RestRecord({
    id:        undefined,
    name:      undefined,
    publish:   undefined,
    createdAt: undefined,
    updatedAt: undefined,
    deletedAt: undefined,
    image:     undefined,
    pivot:     undefined,
    albums:    undefined,
});

const GenreRecord = Record({
    id:        undefined,
    name:      undefined,
    label:     undefined,
    createdAt: undefined,
    updatedAt: undefined,
    deletedAt: undefined,
});

const AlbumFormatRecord = Record({
    name:  undefined,
    label: undefined,
});

const AlbumStatusRecord = Record({
    name:  undefined,
    label: undefined,
});

const ArtistRoleRecord = Record({
    name:  undefined,
    label: undefined,
});

const AlbumTrackRecord = Record({
    albumId:      undefined,
    trackId:      undefined,
    disc:         undefined,
    track:        undefined,
    albumOnly:    undefined,
    preorderDate: undefined,
    releaseDate:  undefined,
});

const ArtistPivotRecord = Record({
    artistId: undefined,
    roleName: undefined,
    isMain:   undefined,
    order:    undefined,
    role:     undefined,
});

const LabelRecord = Record({
    id:   undefined,
    name: undefined,
});

export class Album extends AlbumRecord implements IAlbum {
    public static RELATIONS = [
        "artists",
        "tracks",
        "artwork",
        "format",
        "status",
        "genre",
        "additionalGenre",
        "organisation",
    ];

    public id: number;
    public title: string;
    public titleVersion: string;
    public upc: string;
    public formatName: string;
    public statusName: string;
    public customId: string;
    public genreId: number;
    public additionalGenreId: number;
    public labelId: number;
    public artworkId: number;
    public copyrightArtwork: string;
    public copyrightArtworkYear: number;
    public copyrightSound: string;
    public copyrightSoundYear: number;
    public originalReleaseDate: Date;
    public preorderDate: Date;
    public releaseDate: Date;
    public preorderPreview: boolean;
    public comment: string;
    public commentPrivate: string;
    public readyForDelivery: boolean;
    public organisationId: number;
    public priceId: number;
    public trackPriceId: number;
    public price: any;
    public trackPrice: any;
    public createdAt: Date;
    public updatedAt: Date;
    public deletedAt: Date;
    // Non-DB data
    public artistsStr: string;
    // Relationships
    public artists: number[] | Artist[];
    public tracks: number[] | Track[];
    public artwork: Image;
    public format: AlbumFormat;
    public status: AlbumStatus;
    public genre: Genre;
    public additionalGenre: Genre;
    public organisation: Organisation;
    public label: Label;
    public pivot: AlbumTrack;

    public setViewProperties() {
        let e = this;
        e = e.setCast("viewUrl", e.getViewUrl());
        e = e.setCast("viewText", e.getTitle());
        return e;
    }

    public getTitle(): string {
        return this.title ? this.title : (this.upc ? this.upc : (this.id ? String(this.id) : ""));
    }

    public getViewUrl(): string {
        return this.id ? "/albums/view/" + this.id : null;
    }

    public getEditUrl(): string {
        return this.id ? "/albums/edit/" + this.id : null;
    }

    public getThumbSmallUrl(): string {
        if (this.artwork != null && this.artwork.thumbSmallFile != null) {
            return this.artwork.thumbSmallFile.url;
        }
        return null;
    }

    public getThumbMediumUrl(): string {
        if (this.artwork != null && this.artwork.thumbMediumFile != null) {
            return this.artwork.thumbMediumFile.url;
        }
        return null;
    }

    public isDraft(): boolean {
        return this.statusName === "draft";
    }

    public isArchived(): boolean {
        return this.statusName === "archived";
    }
}

export class Track extends TrackRecord implements ITrack {
    public static RELATIONS = [
        "artists",
        "albums",
        "genre",
        "pivot",
    ];

    public id: number;
    public title: string;
    public titleVersion: string;
    public isrc: string;
    public genreId: number;
    public labelId: number;
    public explicit: boolean;
    public gapless: boolean;
    public readyForDelivery: boolean;
    public rightAvailable: boolean;
    public copyrightSound: string;
    public copyrightSoundYear: number;
    public duration: number;
    public organisationId: number;
    public createdAt: Date;
    public updatedAt: Date;
    public deletedAt: Date;
    // Non-DB data
    public artistsStr: string;
    public durationFormatted: string;
    // Relationships
    public artists: number[] | Artist[];
    public albums: number[] | Album[];
    public genre: Genre;
    public label: Label;
    public pivot: AlbumTrack;

    public getTitle(): string {
        return this.title ? this.title : (this.isrc ? this.isrc : (this.id ? String(this.id) : ""));
    }

    public getViewUrl(): string {
        return this.id ? "/tracks/view/" + this.id : null;
    }
}

export class Artist extends ArtistRecord implements IArtist {
    public static RELATIONS = [
        "albums",
    ];
    public id: number;
    public name: string;
    public publish: boolean;
    public createdAt: Date;
    public updatedAt: Date;
    public deletedAt: Date;
    public image: string;
    // Relationships
    public pivot: ArtistPivot;
    public albums: number[] | Album[];

    public getName(): string {
        return String(this.name ?? this.id ?? "");
    }

    public getViewUrl(): string {
        return this.id ? "/artists/view/" + this.id : null;
    }

    public getImageUrl(): string {
        return this.image;
    }
}

export class Genre extends GenreRecord implements IGenre {
    public id: number;
    public name: string;
    public label: TranslatableMap;
    public createdAt: Date;
    public updatedAt: Date;
    public deletedAt: Date;
}

export class AlbumFormat extends AlbumFormatRecord implements IAlbumFormat {
    public name: string;
    public label: TranslatableMap;
}

export class AlbumStatus extends AlbumStatusRecord implements IAlbumStatus {
    public name: string;
    public label: TranslatableMap;
}

export class ArtistRole extends ArtistRoleRecord implements IArtistRole {
    public name: string;
    public label: TranslatableMap;
}

export class AlbumTrack extends AlbumTrackRecord implements IAlbumTrack {
    public albumId: number;
    public trackId: number;
    public disc: number;
    public track: number;
    public albumOnly: boolean;
    public preorderDate: Date;
    public releaseDate: Date;
}

export class ArtistPivot extends ArtistPivotRecord implements IArtistPivot {
    public artistId: number;
    public roleName: string;
    public isMain: boolean;
    public order: number;
    public role: ArtistRole;
}

export class Label extends LabelRecord implements ILabel {
    public id: number;
    public name: string;
}

export interface MusicInternalState {
    entities: MusicNormalisedEntities;
    lists: {
        albums: number[];
    };
    trackMapping?: Map<number, number>;
}

export interface MusicState {
    music: MusicInternalState;
}

export interface MusicNormalisedEntities extends FilesNormalisedEntities, AuthNormalisedEntities, NormalisedEntities {
    albums?: Map<number, Album>;
    tracks?: Map<number, Track>;
    artists?: Map<number, Artist>;
    genres?: Map<number, Genre>;
    albumFormats?: Map<string, AlbumFormat>;
    albumStatuses?: Map<string, AlbumStatus>;
    albumTracks?: Map<string, AlbumTrack>;
    artistPivots?: Map<string, ArtistPivot>;
    labels?: Map<number, Label>;
}

// Define the normalised version of the entities.
export const NLabel = new schema.Entity("labels");
